import React, { useEffect, useState, useCallback } from "react";
import { Copy, Link2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import StatsCards from "@/components/StatsCards";
import EarningsByHull from "@/components/EarningsByHull";
import EarningsStreams from "@/components/EarningsStreams";
import PayoutsTable from "@/components/PayoutsTable";
import NewReferralForm from "@/components/NewReferralForm";
import ReferralsTable from "@/components/ReferralsTable";
import LinkBuilder from "@/components/LinkBuilder";
import TierCard from "@/components/TierCard";
import DownlineTable from "@/components/DownlineTable";
import HoldStatusCards from "@/components/HoldStatusCards";
import { api } from "@/lib/api";
import { useApp } from "@/lib/AppContext";
import { t } from "@/lib/i18n";

const REGISTER_BASE = "https://eigooinc.net/register?ref=";

export default function ScoutDashboard() {
  const { user, lang } = useApp();
  const [dashboard, setDashboard] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [links, setLinks] = useState([]);

  const refresh = useCallback(async () => {
    const [d, r, p, l] = await Promise.all([
      api.get("/scout/dashboard"),
      api.get("/scout/referrals"),
      api.get("/scout/earnings"),
      api.get("/scout/links"),
    ]);
    setDashboard(d.data);
    setReferrals(r.data);
    setPayouts(p.data);
    setLinks(l.data);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const link = `${REGISTER_BASE}${user?.referral_code || ""}`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} · ${t(lang, "copied")}`);
    } catch {
      toast.error("Clipboard error");
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 space-y-6 lg:space-y-8">
      {/* Hero */}
      <section
        className="relative eigoo-card overflow-hidden p-6 sm:p-8 lg:p-10 anim-fade-up"
        data-testid="scout-hero"
      >
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#d4a843]/10 blur-3xl pointer-events-none" />
        <div className="grid lg:grid-cols-5 gap-6 items-center relative">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#d4a843]/30 bg-[#0f2038] px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[#f3c669]">
              <Sparkles className="w-3 h-3" /> {t(lang, "role_scout")}
            </div>
            <h1 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
              {t(lang, "welcome")},{" "}
              <span className="gold-gradient-text">{user?.name || ""}</span>
            </h1>
            <p className="mt-3 text-slate-300/85 max-w-lg leading-relaxed text-sm sm:text-base">
              ${user?.commission_per_signed_usd || 50}{" "}
              <span className="text-slate-400">{t(lang, "per_signed")}</span> —{" "}
              {t(lang, "earnings_by_hull_sub")}.
            </p>
          </div>

          <div className="lg:col-span-2 space-y-3">
            <div className="rounded-xl border border-[#d4a843]/40 bg-[#0a1628]/70 p-4">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#f3c669]">
                {t(lang, "referral_code")}
              </div>
              <div className="mt-2 flex items-center justify-between gap-3">
                <div
                  data-testid="scout-referral-code"
                  className="font-mono text-2xl sm:text-3xl font-bold gold-gradient-text tracking-widest"
                >
                  {user?.referral_code || "—"}
                </div>
                <button
                  onClick={() => copy(user?.referral_code || "", t(lang, "referral_code"))}
                  className="ghost-btn inline-flex items-center gap-1.5 text-xs"
                  data-testid="copy-code-button"
                >
                  <Copy className="w-3.5 h-3.5" />
                  {t(lang, "copy")}
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-[#1e3a61] bg-[#0f2038]/70 p-4">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                {t(lang, "referral_link")}
              </div>
              <div className="mt-2 flex items-center justify-between gap-3">
                <div
                  data-testid="scout-referral-link"
                  className="font-mono text-xs sm:text-sm text-slate-200 truncate flex items-center gap-1.5"
                  title={link}
                >
                  <Link2 className="w-3.5 h-3.5 text-[#f3c669] shrink-0" />
                  <span className="truncate">{link}</span>
                </div>
                <button
                  onClick={() => copy(link, t(lang, "referral_link"))}
                  className="ghost-btn inline-flex items-center gap-1.5 text-xs shrink-0"
                  data-testid="copy-link-button"
                >
                  <Copy className="w-3.5 h-3.5" />
                  {t(lang, "copy")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <StatsCards data={dashboard} lang={lang} />

      <HoldStatusCards lang={lang} data={dashboard} />

      <TierCard
        lang={lang}
        tier={dashboard?.tier}
        nextTier={dashboard?.next_tier}
        gapUsd={dashboard?.tier_gap_usd}
        gapHint={dashboard?.tier_gap_hint}
        passiveEarnings={dashboard?.passive_earnings_usd}
        downlineCount={dashboard?.downline_count}
        downlineSigned={dashboard?.downline_signed}
      />

      <EarningsStreams
        lang={lang}
        streams={dashboard?.earnings_by_stream || []}
        combinedUsd={dashboard?.combined_earnings_usd || 0}
        combinedVnd={dashboard?.combined_earnings_vnd_approx || 0}
      />

      <LinkBuilder
        lang={lang}
        links={links}
        code={user?.referral_code || ""}
        onChanged={() => refresh()}
      />

      <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
        <NewReferralForm
          hulls={dashboard?.hulls || []}
          lang={lang}
          onCreated={() => refresh()}
        />
        <EarningsByHull
          rows={dashboard?.earnings_by_hull || []}
          hulls={dashboard?.hulls || []}
          lang={lang}
        />
      </div>

      <ReferralsTable rows={referrals} lang={lang} />

      <DownlineTable lang={lang} />

      <PayoutsTable rows={payouts} lang={lang} />

      <footer className="text-center text-xs text-slate-500 pt-4 pb-8">
        © {new Date().getFullYear()} {t(lang, "footer")}
      </footer>
    </main>
  );
}
